import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from "../store/authStore";
import apiClient from '../api/apiClient';
import useChatStore from '../store/chatStore';


function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const NotificationHandler = () => {
  const [employeeId, setEmployeeId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    if (redirect) {
      const role = (useAuthStore.getState().role || 'EMPLOYEE').toLowerCase();
      let target = `/${role}/${redirect}`;

      if (redirect === 'chat') {
        const convId = params.get('conversation_id');
        if (convId) {
          useChatStore.getState().selectConversation(Number(convId));
        }
        target = `/${role}/chat`;
      } else if (redirect === 'profile') {
        const support = params.get('support');
        target = `/${role}/profile${support ? `?support=${support}` : ''}`;
      } else if (redirect === 'pending-leave-list') {
        target = `/${role}/pending-leave-list`;
      } else if (redirect === 'leave-report') {
        target = `/${role}/leave-report`;
      } else if (redirect === 'dashboard') {
        target = `/${role}/dashboard`;
      }

      navigate(target, { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'CHECK_ACTIVE_CONVERSATION') {
        const selectedConvId = useChatStore.getState().selectedConvId;
        const isActive = selectedConvId && Number(selectedConvId) === Number(event.data.conversationId);
        if (event.ports && event.ports[0]) {
          event.ports[0].postMessage({ isActive });
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);


  useEffect(() => {
    // Check if user is logged in
    const checkUserInterval = setInterval(() => {
      const storedEmpId = useAuthStore.getState().employeeId;
      if (storedEmpId !== employeeId) {
        setEmployeeId(storedEmpId);
      }
    }, 1000);

    return () => clearInterval(checkUserInterval);
  }, [employeeId]);

  useEffect(() => {
    if (!employeeId) return;

    // Set up Toast style dynamically
    const styleId = 'custom-toast-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        #custom-toast-container {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 99999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 420px;
          width: calc(100% - 48px);
          pointer-events: none;
        }
        .custom-toast {
          background: rgba(255, 255, 255, 0.94);
          backdrop-filter: blur(16px) saturate(180%);
          -webkit-backdrop-filter: blur(16px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.5);
          border-left: 6px solid #4f46e5;
          border-radius: 16px;
          padding: 16px 20px;
          box-shadow: 
            0 12px 30px -10px rgba(0, 0, 0, 0.15), 
            0 1px 3px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.6);
          display: flex;
          align-items: center;
          gap: 16px;
          pointer-events: auto;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          animation: toastSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .custom-toast:hover {
          transform: translateY(-4px) scale(1.02);
          box-shadow: 
            0 20px 40px -15px rgba(0, 0, 0, 0.2), 
            0 2px 8px rgba(0, 0, 0, 0.05);
          background: rgba(255, 255, 255, 0.98);
        }
        .custom-toast.hide {
          animation: toastSlideOut 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .toast-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(79, 70, 229, 0.08);
          overflow: hidden;
          flex-shrink: 0;
          box-shadow: 0 4px 10px rgba(79, 70, 229, 0.1);
          border: 1px solid rgba(79, 70, 229, 0.15);
        }
        .toast-icon img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 12px;
        }
        .toast-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .toast-title {
          font-weight: 700;
          font-size: 16px;
          color: #1e1b4b;
          font-family: 'Outfit', 'Inter', system-ui, -apple-system, sans-serif;
          letter-spacing: -0.2px;
        }
        .toast-body {
          font-size: 14px;
          color: #374151;
          line-height: 1.45;
          font-weight: 600;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          word-break: break-word;
          max-height: 40px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        .toast-close {
          background: rgba(0, 0, 0, 0.03);
          border: 1px solid rgba(0, 0, 0, 0.05);
          color: #6b7280;
          cursor: pointer;
          font-size: 14px;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          flex-shrink: 0;
          line-height: 1;
          align-self: center;
        }
        .toast-close:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border-color: rgba(239, 68, 68, 0.2);
          transform: rotate(90deg);
        }
        @keyframes toastSlideIn {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes toastSlideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(120%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    // Ensure container exists
    let container = document.getElementById('custom-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'custom-toast-container';
      document.body.appendChild(container);
    }

    const showToast = (title, body, icon, url) => {
      const toast = document.createElement('div');
      toast.className = 'custom-toast';

      // Customize colors based on type
      if (title.toLowerCase().includes('leave')) {
        toast.style.borderLeftColor = '#10b981'; // Green
      } else if (title.toLowerCase().includes('announcement')) {
        toast.style.borderLeftColor = '#f59e0b'; // Amber
      }

      toast.innerHTML = `
        <div class="toast-icon">${icon}</div>
        <div class="toast-content">
          <div class="toast-title">${title}</div>
          <div class="toast-body">${body}</div>
        </div>
        <button class="toast-close">&times;</button>
      `;

      const navigateTo = () => {
        const role = (useAuthStore.getState().role || 'EMPLOYEE').toLowerCase();
        let target = url;
        if (url === 'chat') target = `/${role}/chat`;
        else if (url === 'pending-leave-list') target = `/${role}/pending-leave-list`;
        else if (url === 'leave-report') target = `/${role}/leave-report`;
        else if (url === 'dashboard') target = `/${role}/dashboard`;

        if (target) {
          navigate(target);
        }
      };

      toast.addEventListener('click', (e) => {
        if (e.target.classList.contains('toast-close')) {
          e.stopPropagation();
          removeToast();
        } else {
          navigateTo();
          removeToast();
        }
      });

      const removeToast = () => {
        toast.classList.add('hide');
        setTimeout(() => {
          if (toast.parentNode) {
            toast.parentNode.removeChild(toast);
          }
        }, 300);
      };

      setTimeout(removeToast, 5000);
      container.appendChild(toast);
    };

    // Web Push Activation
    const registerPush = async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('Service worker or Web Push not supported by this browser.');
        return;
      }

      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.warn('Notification permission not granted.');
          return;
        }

        const response = await apiClient.get('/accounts/api/notifications/vapid-key/');
        const vapidPublicKey = response.data.publicKey;

        if (!vapidPublicKey) {
          console.error('No VAPID public key returned from server.');
          return;
        }

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });

        await apiClient.post('/accounts/api/notifications/subscribe/', subscription);
        console.log('Push subscription registered on server.');
      } catch (err) {
        console.error('Error during Web Push registration:', err);
      }
    };

    registerPush();

    // WebSocket setup — prefer REACT_APP_WS_URL, then derive from REACT_APP_API_URL
    const wsBase = process.env.REACT_APP_WS_URL ||
      (process.env.REACT_APP_API_URL || window.location.origin)
        .replace(/^https:\/\//, 'wss://')
        .replace(/^http:\/\//, 'ws://');
    const wsBaseUrl = wsBase.replace(/\/$/, '') + '/ws/notifications/';

    let ws = null;
    let reconnectTimeout = null;
    let isCleanedup = false;

    const connectWebSocket = async () => {
      // Don't attempt connection if the user isn't logged in
      const hasSession = localStorage.getItem("has_session");
      if (!hasSession && !useAuthStore.getState().token) return;

      if (isCleanedup) return;

      // Fetch a short-lived WS ticket via HTTP (cookies work fine here)
      let wsUrl = wsBaseUrl;
      try {
        const ticketRes = await apiClient.get(`/accounts/api/ws-ticket/?_t=${Date.now()}`);
        if (ticketRes.data?.ticket) {
          wsUrl = `${wsBaseUrl}?ws_token=${ticketRes.data.ticket}`;
          console.log('[NotificationHandler] WS ticket obtained');
        }
      } catch (err) {
        console.warn('[NotificationHandler] Could not obtain WS ticket:', err);
      }

      if (isCleanedup) return;
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('Connected to notification WebSocket');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification.new') {
            const { event_type, data: notifData } = data;

            let title = 'Sahvia HRMS';
            let body = '';
            let icon = '🔔';
            let url = '';

            if (event_type === 'chat_message') {
              const selectedConvId = useChatStore.getState().selectedConvId;
              if (selectedConvId && Number(selectedConvId) === Number(notifData.conversation_id)) {
                // User is in the conversation, suppress toast
                return;
              }
              title = notifData.sender_name || 'New Message';
              body = notifData.content || 'Sent a message';
              icon = notifData.sender_photo
                ? `<img src="${notifData.sender_photo}" alt="${title}" onError="this.style.display='none'; this.parentNode.innerHTML='💬';" />`
                : '💬';
              url = 'chat';
            } else if (event_type === 'leave_request') {
              title = 'Leave Request';
              body = `${notifData.applicant_name} has requested a leave.`;
              icon = '📅';
              url = 'pending-leave-list';
            } else if (event_type === 'leave_status_update') {
              title = `Leave Request ${notifData.final_status}`;
              body = `Your leave request for ${notifData.leave_type} has been ${notifData.final_status.toLowerCase()}.`;
              icon = notifData.final_status === 'APPROVED' ? '✅' : '❌';
              url = 'leave-report';
            } else if (event_type === 'holiday_work_request') {
              title = 'Holiday Work Request';
              body = `${notifData.employee_name} requested to work on ${notifData.date}.`;
              icon = '🗓️';
              url = 'allow-over-ride';
            } else if (event_type === 'holiday_work_status_update') {
              title = `Holiday Work Request ${notifData.final_status}`;
              body = `Your holiday work request for ${notifData.date} has been ${notifData.final_status.toLowerCase()}.`;
              icon = notifData.final_status === 'APPROVED' ? '✅' : '❌';
              url = 'dashboard';
            } else if (event_type === 'announcement') {
              title = 'New Announcement';
              body = notifData.title;
              icon = '📢';
              url = 'dashboard';
            }

            showToast(title, body, icon, url);

            // Trigger native browser notification if window/tab is not active
            if (document.visibilityState !== 'visible' && 'Notification' in window && Notification.permission === 'granted') {
              const cleanIcon = notifData.sender_photo || '/favicon.ico';
              const notif = new Notification(title, {
                body: body,
                icon: cleanIcon,
                tag: event_type === 'chat_message' ? `chat-conv-${notifData.conversation_id}` : undefined,
                requireInteraction: true
              });
              notif.onclick = () => {
                window.focus();
                const role = (useAuthStore.getState().role || 'EMPLOYEE').toLowerCase();
                let targetUrl = url;
                if (url === 'chat') targetUrl = `/${role}/chat?conversation_id=${notifData.conversation_id}`;
                else if (url === 'pending-leave-list') targetUrl = `/${role}/pending-leave-list`;
                else if (url === 'leave-report') targetUrl = `/${role}/leave-report`;
                else if (url === 'allow-over-ride') targetUrl = `/${role}/allow-over-ride`;
                else if (url === 'dashboard') targetUrl = `/${role}/dashboard`;
                if (targetUrl) {
                  navigate(targetUrl);
                }
                notif.close();
              };
            }
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = (e) => {
        if (e.code === 4001 || e.code === 4002) {
          console.log('Notification WS unauthenticated. Stopping reconnect.', e.reason);
          return; // Do not reconnect if the session is expired or user inactive
        }
        console.log('Notification WebSocket closed, reconnecting in 5s...', e.reason);
        reconnectTimeout = setTimeout(connectWebSocket, 5000);
      };

      ws.onerror = (err) => {
        console.error('Notification WebSocket error:', err);
        ws.close();
      };
    };

    connectWebSocket();

    return () => {
      isCleanedup = true;
      if (ws) {
        ws.onclose = null;
        ws.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [employeeId, navigate]);


  return null;
};

export default NotificationHandler;
